import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { PDFDownloadLink } from '@react-pdf/renderer';
import api, { endpoints } from '../../services/api';
import ReporteProductividadPDF from '../../components/PDF/ReporteProductividadPDF';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  TextField,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Paper,
  Tabs,
  Tab,
  Chip,
} from '@mui/material';
import {
  Download as DownloadIcon,
  ArrowBack as BackIcon,
  People as PeopleIcon,
  Search as SearchIcon,
  EmojiEvents as TrophyIcon,
} from '@mui/icons-material';

const ReporteProductividad = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState(0);
  const [filters, setFilters] = useState({
    fecha_inicio: new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString().split('T')[0],
    fecha_fin: new Date().toISOString().split('T')[0],
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams(filters).toString();
      const response = await api.get(`${endpoints.reportes.productividad}?${params}`);
      setData(response.data.data);
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (e) => {
    setFilters((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const getRankBadge = (index) => {
    if (index === 0) return <TrophyIcon sx={{ color: '#FFD700' }} />;
    if (index === 1) return <TrophyIcon sx={{ color: '#C0C0C0' }} />;
    if (index === 2) return <TrophyIcon sx={{ color: '#CD7F32' }} />;
    return <Chip label={index + 1} size="small" />;
  };

  return (
    <Box>
      {/* Header */}
      <Card
        sx={{
          mb: 3,
          background: 'linear-gradient(135deg, #7B1FA2 0%, #6A1B9A 100%)',
          color: 'white',
        }}
      >
        <CardContent sx={{ p: 4 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <PeopleIcon sx={{ fontSize: 48 }} />
            <Box>
              <Typography variant="h4" fontWeight="bold">
                Reporte de Productividad
              </Typography>
              <Typography sx={{ opacity: 0.9 }}>
                Rendimiento de paletizadores y distribuidores
              </Typography>
            </Box>
          </Box>
        </CardContent>
      </Card>

      {/* Filters */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={4}>
              <TextField
                label="Fecha Inicio"
                type="date"
                name="fecha_inicio"
                value={filters.fecha_inicio}
                onChange={handleFilterChange}
                fullWidth
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                label="Fecha Fin"
                type="date"
                name="fecha_fin"
                value={filters.fecha_fin}
                onChange={handleFilterChange}
                fullWidth
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <Button
                variant="contained"
                onClick={fetchData}
                startIcon={<SearchIcon />}
                fullWidth
                sx={{ height: 56 }}
              >
                Buscar
              </Button>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Resumen */}
      {data?.resumen && (
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={6}>
            <Paper sx={{ p: 3, textAlign: 'center', bgcolor: 'primary.50' }}>
              <Typography variant="h3" fontWeight="bold" color="primary.main">
                {data.resumen.total_paletizadores}
              </Typography>
              <Typography color="text.secondary">Paletizadores Activos</Typography>
            </Paper>
          </Grid>
          <Grid item xs={6}>
            <Paper sx={{ p: 3, textAlign: 'center', bgcolor: 'secondary.50' }}>
              <Typography variant="h3" fontWeight="bold" color="secondary.main">
                {data.resumen.total_distribuidores}
              </Typography>
              <Typography color="text.secondary">Distribuidores Activos</Typography>
            </Paper>
          </Grid>
        </Grid>
      )}

      {/* Tabs */}
      <Card sx={{ mb: 3 }}>
        <Tabs value={activeTab} onChange={(e, v) => setActiveTab(v)} sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tab label="Paletizadores" />
          <Tab label="Distribuidores" />
        </Tabs>

        {/* Paletizadores */}
        {activeTab === 0 && (
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow sx={{ bgcolor: 'primary.50' }}>
                  <TableCell sx={{ width: 60 }}>Rank</TableCell>
                  <TableCell>Nombre</TableCell>
                  <TableCell align="center">Pallets Armados</TableCell>
                  <TableCell align="center">Vuelos</TableCell>
                  <TableCell align="right">Peso Total (kg)</TableCell>
                  <TableCell align="right">Piezas</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                      <CircularProgress />
                    </TableCell>
                  </TableRow>
                ) : data?.paletizadores?.length > 0 ? (
                  data.paletizadores.map((p, idx) => (
                    <TableRow key={p.id} hover sx={{ bgcolor: idx < 3 ? 'warning.50' : 'inherit' }}>
                      <TableCell>{getRankBadge(idx)}</TableCell>
                      <TableCell>
                        <Typography fontWeight={idx < 3 ? 'bold' : 'normal'}>
                          {p.nombre}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Chip label={p.pallets_armados} color="primary" />
                      </TableCell>
                      <TableCell align="center">{p.vuelos_trabajados}</TableCell>
                      <TableCell align="right">{(p.peso_total || 0).toLocaleString()}</TableCell>
                      <TableCell align="right">{(p.piezas_total || 0).toLocaleString()}</TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                      <Typography color="text.secondary">No hay datos</Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        {/* Distribuidores */}
        {activeTab === 1 && (
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow sx={{ bgcolor: 'secondary.50' }}>
                  <TableCell sx={{ width: 60 }}>Rank</TableCell>
                  <TableCell>Nombre</TableCell>
                  <TableCell align="center">Pallets Distribuidos</TableCell>
                  <TableCell align="center">Vuelos</TableCell>
                  <TableCell align="right">Peso Total (kg)</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                      <CircularProgress />
                    </TableCell>
                  </TableRow>
                ) : data?.distribuidores?.length > 0 ? (
                  data.distribuidores.map((d, idx) => (
                    <TableRow key={d.id} hover sx={{ bgcolor: idx < 3 ? 'warning.50' : 'inherit' }}>
                      <TableCell>{getRankBadge(idx)}</TableCell>
                      <TableCell>
                        <Typography fontWeight={idx < 3 ? 'bold' : 'normal'}>
                          {d.nombre}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Chip label={d.pallets_distribuidos} color="secondary" />
                      </TableCell>
                      <TableCell align="center">{d.vuelos_trabajados}</TableCell>
                      <TableCell align="right">{(d.peso_total || 0).toLocaleString()}</TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                      <Typography color="text.secondary">No hay datos</Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Card>

      {/* Actions */}
      <Box sx={{ display: 'flex', gap: 2 }}>
        {data && (
          <PDFDownloadLink
            document={<ReporteProductividadPDF data={data} filters={filters} />}
            fileName={`reporte-productividad-${filters.fecha_inicio}-${filters.fecha_fin}.pdf`}
            style={{ textDecoration: 'none' }}
          >
            {({ loading: pdfLoading }) => (
              <Button
                variant="contained"
                color="success"
                startIcon={pdfLoading ? <CircularProgress size={20} color="inherit" /> : <DownloadIcon />}
                disabled={pdfLoading}
              >
                {pdfLoading ? 'Generando...' : 'Descargar PDF'}
              </Button>
            )}
          </PDFDownloadLink>
        )}
        <Button component={Link} to="/reportes" variant="contained" color="inherit" startIcon={<BackIcon />}>
          Volver
        </Button>
      </Box>
    </Box>
  );
};

export default ReporteProductividad;

